import { expandLocales, type Translate } from '../ui/localized';
import type { Lang } from '../ui/i18n';
import type { CatalogueLine } from '../config/collections';

/** A42F catalogue page copy. Model and colourway names are data and are never translated. */
export interface CatalogueCopy {
  pageTitle: string;
  eyebrow: string;
  title: string;
  lede: string;
  gridLabel: string;
  tryOn: string;
  tryOnLabel: (frame: string) => string;
  footer: (n: number) => string;
  debug: string;
}

export const CATALOGUE_COPY: Record<'en' | 'fr' | 'id', CatalogueCopy> = {
  en: {
    pageTitle: 'Maison Mata — Handmade virtual try-on',
    eyebrow: 'Maison Mata · Handmade · Virtual try-on',
    title: 'Try every Handmade frame, live.',
    lede: 'Shaped by hand in our Canggu atelier. Tap a frame to see it on your face — your camera stays on your device, nothing is sent or recorded.',
    gridLabel: 'Handmade frames',
    tryOn: 'Try it on',
    tryOnLabel: frame => `Try on ${frame}`,
    footer: n => `${n} Handmade frames · Works on iPhone, Android and desktop`,
    debug: 'Debug settings',
  },
  fr: {
    pageTitle: 'Maison Mata — Essayage virtuel Handmade',
    eyebrow: 'Maison Mata · Handmade · Essayage virtuel',
    title: 'Essayez chaque monture Handmade, en direct.',
    lede: 'Façonnées à la main dans notre atelier de Canggu. Choisissez une monture pour la voir sur votre visage — votre caméra reste sur votre appareil, rien n’est envoyé ni enregistré.',
    gridLabel: 'Montures Handmade',
    tryOn: 'Essayer',
    tryOnLabel: frame => `Essayer ${frame}`,
    footer: n => `${n} montures Handmade · Fonctionne sur iPhone, Android et ordinateur`,
    debug: 'Réglages de débogage',
  },
  id: {
    pageTitle: 'Maison Mata — Coba virtual Handmade',
    eyebrow: 'Maison Mata · Handmade · Coba virtual',
    title: 'Coba setiap bingkai Handmade, secara langsung.',
    lede: 'Dibuat dengan tangan di atelier kami di Canggu. Pilih bingkai untuk melihatnya di wajah Anda — kamera tetap di perangkat Anda, tidak ada yang dikirim atau direkam.',
    gridLabel: 'Bingkai Handmade',
    tryOn: 'Coba',
    tryOnLabel: frame => `Coba ${frame}`,
    footer: n => `${n} bingkai Handmade · Berfungsi di iPhone, Android, dan desktop`,
    debug: 'Pengaturan debug',
  },
};

/**
 * A200 — the Signature catalogue page (its own page, never mixed with the Handmade one).
 * Wording from the Signature product pages: designed by the Maison Mata team, Mazzucchelli acetate.
 */
export const SIGNATURE_COPY: Record<'en' | 'fr' | 'id', CatalogueCopy> = {
  en: {
    pageTitle: 'Maison Mata — Signature virtual try-on',
    eyebrow: 'Maison Mata · Signature · Virtual try-on',
    title: 'Try every Signature frame, live.',
    lede: 'Designed by our team in Bali, in Italian Mazzucchelli acetate. Tap a frame to see it on your face — your camera stays on your device, nothing is sent or recorded.',
    gridLabel: 'Signature frames',
    tryOn: 'Try it on',
    tryOnLabel: frame => `Try on ${frame}`,
    footer: n => `${n} Signature frames · Works on iPhone, Android and desktop`,
    debug: 'Debug settings',
  },
  fr: {
    pageTitle: 'Maison Mata — Essayage virtuel Signature',
    eyebrow: 'Maison Mata · Signature · Essayage virtuel',
    title: 'Essayez chaque monture Signature, en direct.',
    lede: 'Dessinées par notre équipe à Bali, en acétate italien Mazzucchelli. Choisissez une monture pour la voir sur votre visage — votre caméra reste sur votre appareil, rien n’est envoyé ni enregistré.',
    gridLabel: 'Montures Signature',
    tryOn: 'Essayer',
    tryOnLabel: frame => `Essayer ${frame}`,
    footer: n => `${n} montures Signature · Fonctionne sur iPhone, Android et ordinateur`,
    debug: 'Réglages de débogage',
  },
  id: {
    pageTitle: 'Maison Mata — Coba virtual Signature',
    eyebrow: 'Maison Mata · Signature · Coba virtual',
    title: 'Coba setiap bingkai Signature, secara langsung.',
    lede: 'Dirancang oleh tim kami di Bali, dari asetat Italia Mazzucchelli. Pilih bingkai untuk melihatnya di wajah Anda — kamera tetap di perangkat Anda, tidak ada yang dikirim atau direkam.',
    gridLabel: 'Bingkai Signature',
    tryOn: 'Coba',
    tryOnLabel: frame => `Coba ${frame}`,
    footer: n => `${n} bingkai Signature · Berfungsi di iPhone, Android, dan desktop`,
    debug: 'Pengaturan debug',
  },
};

export function catalogueCopy(line: CatalogueLine, lang: Lang): CatalogueCopy {
  return (line === 'Classic' ? ALL_SIGNATURE_COPY : ALL_CATALOGUE_COPY)[lang];
}

const ALL_CATALOGUE_COPY = expandLocales<CatalogueCopy>(CATALOGUE_COPY, (tr: Translate) => ({
    pageTitle: tr("Maison Mata \u2014 Handmade virtual try-on"),
    eyebrow: tr("Maison Mata \u00B7 Handmade \u00B7 Virtual try-on"),
    title: tr("Try every Handmade frame, live."),
    lede: tr("Shaped by hand in our Canggu atelier. Tap a frame to see it on your face \u2014 your camera stays on your device, nothing is sent or recorded."),
    gridLabel: tr("Handmade frames"),
    tryOn: tr("Try it on"),
    tryOnLabel: frame => tr("Try on {0}", [frame]),
    footer: n => tr("{0} Handmade frames \u00B7 Works on iPhone, Android and desktop", [n]),
    debug: tr("Debug settings"),
}));

const ALL_SIGNATURE_COPY = expandLocales<CatalogueCopy>(SIGNATURE_COPY, (tr: Translate) => ({
    pageTitle: tr("Maison Mata \u2014 Signature virtual try-on"),
    eyebrow: tr("Maison Mata \u00B7 Signature \u00B7 Virtual try-on"),
    title: tr("Try every Signature frame, live."),
    lede: tr("Designed by our team in Bali, in Italian Mazzucchelli acetate. Tap a frame to see it on your face \u2014 your camera stays on your device, nothing is sent or recorded."),
    gridLabel: tr("Signature frames"),
    tryOn: tr("Try it on"),
    tryOnLabel: frame => tr("Try on {0}", [frame]),
    footer: n => tr("{0} Signature frames \u00B7 Works on iPhone, Android and desktop", [n]),
    debug: tr("Debug settings"),
}));
